// frontend/src/api/databaseClient.js
// Created: April 8, 2025
// This client connects directly to the Railway PostgreSQL database

import axios from 'axios';
import { getToken } from '../auth/auth';
import { API_URL } from '../config';

// Use the API URL from config
const BASE_URL = API_URL;

// Add the /api prefix to ensure all requests go to the right endpoint
const API_BASE_URL = `${BASE_URL}/api`;

console.log('Database client using base URL:', API_BASE_URL);

// Create a simple cache for GET requests
const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const databaseClient = axios.create({
  baseURL: API_BASE_URL,  // Already has /api prefix
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  timeout: 60000, // Increased from 30 seconds to 60 seconds
  withCredentials: true
});

// Add request interceptor for auth token and caching
databaseClient.interceptors.request.use(config => {
  const token = getToken();
  console.log('Database request interceptor - Token exists:', !!token);
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Added Authorization header:', `Bearer ${token.substring(0, 10)}...`);
  } else {
    console.log('No token available for database request to:', config.url);
  }
  
  // Only cache GET requests
  if (config.method === 'get') {
    const cacheKey = `${config.url}${JSON.stringify(config.params || {})}`;
    const cachedResponse = requestCache.get(cacheKey);
    
    if (cachedResponse && Date.now() - cachedResponse.timestamp < CACHE_DURATION) {
      return Promise.reject({
        __CACHE_HIT: true,
        data: cachedResponse.data
      });
    }
  }
  
  return config;
}, error => {
  console.error('Database request error:', error);
  return Promise.reject(error);
});

// Add response interceptor to handle errors and caching
databaseClient.interceptors.response.use(
  response => {
    console.log('Database response interceptor - Status:', response.status);
    
    // Cache successful GET responses
    if (response.config.method === 'get') {
      const cacheKey = `${response.config.url}${JSON.stringify(response.config.params || {})}`;
      requestCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
    }
    
    return response;
  },
  error => {
    console.error('Database response error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data
    });

    // Handle cache hits
    if (error.__CACHE_HIT) {
      return Promise.resolve({ data: error.data });
    }

    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        success: false,
        message: 'Request timed out. Please try again.'
      });
    }
    
    if (error.response) {
      // Handle rate limiting with exponential backoff
      if (error.response.status === 429) {
        const retryAfter = parseInt(error.response.headers['retry-after'] || '5', 10);
        console.log(`Rate limited. Will retry after ${retryAfter} seconds.`);
        
        // Return a promise that resolves after the retry period
        return new Promise(resolve => {
          setTimeout(() => {
            // Retry the request
            resolve(databaseClient(error.config));
          }, retryAfter * 1000);
        });
      }
      
      // Handle authentication errors
      if (error.response.status === 401) {
        console.log('Database 401 Unauthorized error details:', {
          url: error.config.url,
          message: error.response.data?.message,
          token: getToken() ? 'Token exists' : 'No token'
        });
        
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject({
          success: false,
          message: 'Session expired. Please log in again.'
        });
      }
      
      // Return error response data if available
      if (error.response.data) {
        return Promise.reject(error.response.data);
      }
    }
    
    // Handle network errors
    if (error.request) {
      console.error('Database Network Error:', error.request);
      return Promise.reject({
        success: false,
        message: 'Network error. Please check your connection.'
      });
    }
    
    return Promise.reject(error);
  }
);

export default databaseClient;